import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classnames from 'classnames';
import { getSortedPhotos } from '../helpers/getSortedPhotos';
import { getSrcSetStringXDescriptors } from 'dibs-image-utils/exports/srcSet';
import { showVideo } from '../../utils/videoUtils';

// components
import { Carousel } from 'dibs-carousel';
import { PdpRespIconBubblesWrapper } from '../PdpRespIconBubbles/PdpRespIconBubblesWrapper';
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import LazyLoad from 'react-lazy-load';
import { PdpRespZoomModalLoader } from '../PdpRespZoomModal/PdpRespZoomModalLoader';
import { PdpRespHeroCarouselRequestMoreImages } from './PdpRespHeroCarouselRequestMoreImages';
import { ViewInRoomImage } from 'dibs-view-in-room/exports/ViewInRoomImage';
import { ViewInRoomImageCredits } from 'dibs-view-in-room/exports/ViewInRoomImageCredits';
import { PdpRespHeroCarouselVimeoLazyLoader } from './PdpRespHeroCarouselVimeoLazyLoader';
import { PdpSharedHeroCarouselThumb } from './PdpSharedHeroCarouselThumb';
import { SharedCarouselDot } from '../../shared/SharedCarouselDot/SharedCarouselDot';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

import { ResponsiveImage, FetchPriorityValues } from '../../components/global/ResponsiveImage';

// helpers
import {
    trackClickCarousel,
    trackChangeCarousel,
    trackViewRoomDisplayed,
    trackViewRoomClick,
} from './heroCarouselTracking';
import videoTracking from './videoTracking';
import { charLimitedString } from '../helpers/charLimitedString';

// constants
import { HORIZONTAL_OFFSET_RESP_IMG } from '../../constants/imageConstants';

// styles
import styles from './PdpRespHeroCarousel.scss';

// messages
const counter = (index, totalItems) => (
    <FormattedMessage
        id="PdpRespHeroCarousel.counterMedia"
        defaultMessage="{position, number} of {totalItems, number}"
        values={{ position: index + 1, totalItems }}
    />
);

const zoomLabel = (
    <FormattedMessage id="PdpRespHeroCarousel.zoomLabel" defaultMessage="view larger image of " />
);

const MAX_THUMBS_PER_PAGE = 6;
const MEDIA_TYPE_VIEW_IN_ROOM = 'viewInRoom';
const MEDIA_TYPE_VIDEO = 'VIMEO';

export class PdpRespHeroCarouselComponent extends Component {
    constructor(props) {
        super(props);

        this.onRoomClick = this.onRoomClick.bind(this);
        this.onImageClick = this.onImageClick.bind(this);
        this.onIndexChange = this.onIndexChange.bind(this);
        this.onModalCloseClick = this.onModalCloseClick.bind(this);
        this.renderCarouselItem = this.renderCarouselItem.bind(this);
        this.renderCarouselThumb = this.renderCarouselThumb.bind(this);
        this.renderCarouselThumbsList = this.renderCarouselThumbsList.bind(this);

        this.state = {
            index: 0,
            isJumpToItemForced: false,
            modalOpen: false,
            windowWidth: null,
        };
        this.setInstanceProperties(props, true);
    }

    componentDidMount() {
        this.setState({ windowWidth: document.documentElement.clientWidth });
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setInstanceProperties(nextProps);
    }

    setInstanceProperties(props, disableStatefulOperations) {
        const { item = {}, viewer, selectedSkuId } = props;
        const { viewInRoomPhotos, isMultiSku, defaultSkuId, seller } = item;
        const activeSkuId = selectedSkuId || defaultSkuId;
        const prevPhotos = this._photos || item?.sortedPhotos;
        this._photos = isMultiSku ? getSortedPhotos(activeSkuId, prevPhotos) : item.photos || [];
        const firstPhotoChanged =
            !!prevPhotos &&
            !!this._photos &&
            prevPhotos?.[0]?.serviceId !== this._photos?.[0]?.serviceId;
        if (firstPhotoChanged && !disableStatefulOperations) {
            this.resetThumbCarousel();
        }
        const videoVimeo = item?.videoVimeo;
        const displayVideo = showVideo({
            videoUploadToolsIsOn: viewer?.videoUploadToolsFeatureFlag,
            vimeoId: videoVimeo?.vimeoId,
            videoStatus: videoVimeo?.status,
            videoReviewStatus: item?.reviews?.videoReview?.status,
        });
        this._videoVimeo = videoVimeo;

        const carouselPhotos = this._photos.filter(({ largePath }) => largePath);
        this._mediaZoom = carouselPhotos.map(({ masterOrZoomPath: src, alt }) => ({
            src,
            alt,
        }));
        this._media = [
            ...carouselPhotos.map(({ largePath, masterOrZoomPath, alt }) => ({
                src: largePath,
                alt,
                photo: {
                    largePath,
                    masterOrZoomPath,
                },
            })),
            ...viewInRoomPhotos.map((obj, index) => ({
                ...obj,
                roomIndex: index,
                type: MEDIA_TYPE_VIEW_IN_ROOM,
            })),
        ];
        this._mediaThumbs = [
            ...carouselPhotos.map(({ largePath, masterOrZoomPath, alt } = {}) => ({
                src: largePath,
                srcSet: getSrcSetStringXDescriptors({ url: masterOrZoomPath }),
                alt,
            })),
            ...viewInRoomPhotos.map(({ src, srcSetProps, credits }) => ({
                src,
                alt: credits,
                srcSet: srcSetProps?.srcSet,
                type: MEDIA_TYPE_VIEW_IN_ROOM,
            })),
        ];

        if (displayVideo) {
            const video = {
                src: this._videoVimeo.fileByVideoSize?.link,
                thumbnail: this._videoVimeo?.thumbnail,
                type: MEDIA_TYPE_VIDEO,
            };
            // default insert as second item
            let targetVimeoIndex = 1;
            if (isMultiSku) {
                // get first product image index and add video before it
                targetVimeoIndex = carouselPhotos.findIndex(
                    ({ imageSkuXrefs }) => !imageSkuXrefs.length
                );
            }
            this._media.splice(targetVimeoIndex, 0, video);
            const id = this._videoVimeo?.vimeoId;
            this._mediaZoom.splice(targetVimeoIndex, 0, {
                ...video,
                vimeoPlayerOptions: {
                    quality: '4k',
                    id,
                    tracking: videoTracking,
                },
            });
            this._mediaThumbs.splice(targetVimeoIndex, 0, video);
        }

        this._isRequestMoreImagesEnabled = seller?.isAccessible && !item?.hasPrivateListing;
        this._totalItems = this._isRequestMoreImagesEnabled
            ? this._media.length + 1
            : this._media.length;
    }

    /**
     * Handler for when close 'x' is clicked (or ESC is pressed)
     */
    onModalCloseClick({ index }) {
        this.setState({ index, isJumpToItemForced: true, modalOpen: false });
    }
    /**
     * Handler for when image is clicked
     *
     * @param {number} index - clicked index
     */
    onImageClick({ index }, event) {
        this.setState({ modalOpen: true }, () => {
            // fire tracking
            trackClickCarousel(
                {
                    index,
                    total: this._totalItems,
                },
                event
            );
        });
    }
    /**
     * Handler for when room is clicked
     */
    onRoomClick({ index }, event) {
        this.setState({ modalOpen: true }, () => {
            trackViewRoomClick(
                {
                    index,
                    total: this._totalItems,
                },
                event
            );
        });
    }
    /**
     * Handler for when image is changed
     *
     * @param {number} index - new index
     * @param {string} change - either 'previous' or 'next'
     */
    onIndexChange({ change, index }) {
        const {
            item: { viewInRoomPhotos },
        } = this.props;
        const total = this._totalItems;

        this.setState({ index, isJumpToItemForced: false }, () => {
            trackChangeCarousel({
                index,
                total,
                next: change === 'next',
            });
            if (index >= this._media.length - viewInRoomPhotos.length && !!this._media[index]) {
                trackViewRoomDisplayed({ index, total });
            }
        });
    }
    renderCarouselItem({ index, isCurrentItem }) {
        const position = index + 1;
        const {
            item,
            user,
            disableZoomModal,
            disableRequestMoreImagesModal,
            isCondensedCarousel,
            relayVariables: { isClient, isCondensedPdpRedesignVariant },
        } = this.props;
        const { viewInRoomPhotos, isSold: isSoldPdp, isUnpurchasable, description } = item;
        const { src, alt, type, roomIndex, photo } = this._media[index] || {};
        const isRequestMoreImagesEnabled = this._isRequestMoreImagesEnabled;
        const isCarouselLastItem = position === this._totalItems;
        const isRequestMoreImages = isCarouselLastItem && isRequestMoreImagesEnabled;
        const isShowViewInRoom = type === MEDIA_TYPE_VIEW_IN_ROOM;
        const isVideo = type === MEDIA_TYPE_VIDEO;
        const shortDescription = description ? charLimitedString(description, 250) : '';
        const dataTn = `pdp-image-carousel-image-${position}`;

        const isUnavailablePdp = isSoldPdp || isUnpurchasable;

        const wrapperProps = {
            className: classnames(styles.item, {
                [styles.smallCarouselItem]: isUnavailablePdp,
                [styles.moreImages]: (isRequestMoreImages || isShowViewInRoom) && !isUnavailablePdp,
                [styles.smallCarouselMoreImages]:
                    (isRequestMoreImages || isShowViewInRoom) && isUnavailablePdp,
                'is-zoom': !isRequestMoreImages,
                [styles.condensedCarousel]: isCondensedCarousel,
            }),
            onClick:
                isRequestMoreImages || disableZoomModal
                    ? () => {}
                    : event => this.onImageClick({ index }, event),
        };

        let img = null;

        if (photo) {
            img = (
                <figure className={styles.figureWrapper}>
                    <picture>
                        <ResponsiveImage
                            dataTn="pdp-hero-carousel-image"
                            className={
                                isUnavailablePdp
                                    ? styles.smallCarouselphotoSold
                                    : classnames(styles.photo, {
                                          [styles.photoVariant]: isCondensedPdpRedesignVariant,
                                      })
                            }
                            alt={alt}
                            photo={photo}
                            isClient={isClient}
                            sizes={'75vw'}
                            widths={[640, 800, 960, 1318]}
                            fetchPriority={FetchPriorityValues.HIGH}
                        />
                    </picture>
                    <figcaption>{shortDescription}</figcaption>
                </figure>
            );
        }

        let mediaElement = null;

        if (img) {
            // Don't want to lazy load 1st image
            mediaElement =
                index === 0 ? (
                    img
                ) : (
                    <Fragment>
                        <LazyLoad
                            className={styles.lazyLoad}
                            offsetHorizontal={HORIZONTAL_OFFSET_RESP_IMG}
                            key={index}
                        >
                            {img}
                        </LazyLoad>
                        <noscript
                            dangerouslySetInnerHTML={{
                                __html: `<img src="${src}" alt="${alt}" />`,
                            }}
                        />
                    </Fragment>
                );
        }

        if (isShowViewInRoom) {
            const currentPhoto = viewInRoomPhotos[roomIndex];

            return (
                <div className={styles.viewInRoomWrapper}>
                    <Link
                        dataTn={dataTn}
                        onClick={wrapperProps.onClick}
                        className={classnames(wrapperProps.className, {
                            [styles.viewInRoomImageWrapper]: !isUnavailablePdp,
                            [styles.viewInRoomImageWrapperSoldPdp]: isUnavailablePdp,
                        })}
                    >
                        <ViewInRoomImage
                            showDimensionsOnHover
                            useEagerImageLoad={false}
                            srcSetSizes="75vw"
                            srcSetWidths={[640, 800, 960, 1318]}
                            item={item}
                            user={user}
                            viewInRoomPhoto={currentPhoto}
                            mainImagePaths={{
                                imagePath: this._media[0]?.photo?.largePath,
                                masterOrZoomPath: this._media[0]?.photo?.masterOrZoomPath,
                            }}
                            className={styles.viewInRoomImage}
                        />
                    </Link>
                    <ViewInRoomImageCredits
                        item={item}
                        user={user}
                        viewInRoomPhoto={currentPhoto}
                    />
                </div>
            );
        } else if (isVideo && isClient) {
            mediaElement = (
                <PdpRespHeroCarouselVimeoLazyLoader
                    isCurrentItem={isCurrentItem}
                    id={this._videoVimeo?.vimeoId}
                    isSmallCarousel={isUnavailablePdp}
                />
            );
        } else if (isRequestMoreImages) {
            return (
                <div {...wrapperProps} data-tn={dataTn}>
                    <PdpRespHeroCarouselRequestMoreImages
                        disableRequestMoreImagesModal={disableRequestMoreImagesModal}
                        item={item}
                        user={user}
                    />
                </div>
            );
        } else {
            // if standard image + zoom modal, make each element accessibly interactive
            return (
                <Link {...wrapperProps} dataTn={dataTn}>
                    <span className={styles.hidden}>{zoomLabel}</span>
                    {mediaElement}
                </Link>
            );
        }
        // if video, showRoom, or requestMore, do not wrap in button bc content will
        // have its own interactive element(s)
        return (
            <div {...wrapperProps} data-tn={dataTn}>
                {mediaElement}
            </div>
        );
    }

    renderCarouselThumb({ index, isCurrentItem }) {
        const {
            item,
            relayVariables: { isClient },
        } = this.props;
        const thumbSrc = this._mediaThumbs?.[index]?.src;
        const { type } = this._media[index] || {};
        const isRoomView = type === MEDIA_TYPE_VIEW_IN_ROOM;
        const isVideo = type === MEDIA_TYPE_VIDEO;
        const { isSold: isSoldPdp, isUnpurchasable } = item;
        const isUnavailablePdp = isSoldPdp || isUnpurchasable;

        return (
            <PdpSharedHeroCarouselThumb
                key={`${thumbSrc}-${index}`}
                index={index}
                isClient={isClient}
                thumbs={this._mediaThumbs}
                totalItems={this._totalItems}
                onIndexChange={() => this.onIndexChange({ index })}
                isCurrentItem={isCurrentItem}
                isRequestMoreImagesEnabled={this._isRequestMoreImagesEnabled}
                isRoomView={isRoomView}
                isVideo={isVideo}
                size={this.props.isCondensedCarousel || isUnavailablePdp ? 'medium' : 'large'}
            />
        );
    }

    resetThumbCarousel() {
        this.setState({ index: 0, isJumpToItemForced: true });
    }

    renderCarouselThumbsList({ index }) {
        const items = [];
        for (let i = 0; i < this._totalItems; i++) {
            items.push(this.renderCarouselThumb({ index: i, isCurrentItem: i === index }));
        }
        return items;
    }

    render() {
        const {
            itemTitle,
            itemImage,
            environment,
            relayVariables,
            hideArrows,
            showDots,
            showCounter,
            item,
            user,
            viewer,
            selectedSkuId,
            hideBubbles,
            hideThumbs,
            isCondensedCarousel,
            verticalThumbsPerPage = MAX_THUMBS_PER_PAGE,
        } = this.props;
        const { defaultSkuId, isSold: isSoldPdp, isUnpurchasable } = item;
        const isUnavailablePdp = isSoldPdp || isUnpurchasable;

        const { index, modalOpen, windowWidth } = this.state;
        const { itemId = '', isClient, isTrade, isCondensedPdpRedesignVariant } =
            relayVariables || {};
        const renderDot = showDots
            ? ({ isCurrentDot = false } = {}) => <SharedCarouselDot isActive={isCurrentDot} />
            : null;

        const isVerticallyAligned = windowWidth > 1024 || isUnavailablePdp;
        const wrapperClassName = classnames(styles.wrapper, 'enable-click-on-preview', {
            [styles.condensedCarouselWrapper]: isCondensedCarousel,
            [styles.isHorizontallyAligned]: isClient && !isVerticallyAligned,
            [styles.isUnavailablePdp]: isUnavailablePdp,
            [styles.isCondensedPdpRedesignVariant]: isCondensedPdpRedesignVariant,
        });
        const thumbsCarousel =
            this._totalItems > verticalThumbsPerPage ? (
                <Carousel
                    itemKey="hero-item-thumb"
                    dataTn="pdp-image-carousel-thumbs"
                    index={index}
                    totalItems={this._totalItems}
                    itemsPerPage={isVerticallyAligned ? verticalThumbsPerPage : MAX_THUMBS_PER_PAGE}
                    slideCarouselItems
                    enableItemControl
                    hideArrows
                    isJumpToItemForced={this.state.isJumpToItemForced}
                    onIndexChange={this.onIndexChange}
                    renderItem={this.renderCarouselThumb}
                    isVerticallyAligned={isVerticallyAligned}
                />
            ) : (
                <div className={styles.thumbsList}>{this.renderCarouselThumbsList({ index })}</div>
            );
        const thumbsClass = classnames(styles.thumbs, {
            [styles.isVerticallyAligned]: isVerticallyAligned,
            [styles.isHorizontallyAligned]: isClient && !isVerticallyAligned,
            [styles.condensedCarousel]: isCondensedCarousel,
            [styles.isUnavailablePdp]: isUnavailablePdp,
        });
        const thumbsComponent = <div className={thumbsClass}>{isClient && thumbsCarousel}</div>; // render wrapper with fixed width to prevent layout shift

        return (
            <div className={wrapperClassName}>
                {/* Vertical Thumbs */}
                {!hideThumbs && (
                    <>
                        {isVerticallyAligned ? (
                            thumbsComponent
                        ) : (
                            <div className={`${thumbsClass} ${styles.verticalThumbsPlaceholder}`} /> // placeholder to prevent layout shift when screen width is calculated (only need one for vertical thumbs since horizontal is the default)
                        )}
                    </>
                )}

                <div className={styles.mainImageWrapper}>
                    {/* Zoom Modal */}
                    <PdpRespZoomModalLoader
                        environment={environment}
                        relayVariables={relayVariables}
                        isOpen={modalOpen}
                        totalItems={this._totalItems}
                        onCloseClick={this.onModalCloseClick}
                        startingIndex={index}
                        zoomMedia={this._mediaZoom}
                        display
                        itemTitle={itemTitle}
                        itemImage={itemImage}
                        itemId={itemId}
                    />
                    {/* Images */}
                    <Carousel
                        itemKey="hero-item"
                        dataTn="pdp-image-carousel"
                        index={index}
                        totalItems={this._totalItems}
                        onIndexChange={this.onIndexChange}
                        renderItem={this.renderCarouselItem}
                        hideArrows={hideArrows}
                        showDots={showDots}
                        renderDot={renderDot}
                        classNames={
                            isUnavailablePdp
                                ? {
                                      wrapper: styles.smallCarouselWrapper,
                                      listWrapper: styles.smallCarouselListWrapper,
                                  }
                                : undefined
                        }
                    />
                    {/* Counter */}
                    {showCounter && (
                        <div
                            className={classnames(styles.counter, {
                                [styles.isUnavailablePdp]: isUnavailablePdp,
                            })}
                            data-tn="pdp-image-carousel-count-text"
                        >
                            {counter(index, this._totalItems)}
                        </div>
                    )}
                    {/* Horizontal Thumbs */}
                    {isClient && !isVerticallyAligned && thumbsComponent}
                    {/* Social Icons / Print */}
                    {!hideBubbles && (
                        <div className={styles.printHide}>
                            <PdpRespIconBubblesWrapper
                                relayVariables={{
                                    itemId,
                                    isTrade,
                                    isClient,
                                }}
                                skuId={selectedSkuId || defaultSkuId}
                                item={item}
                                user={user}
                                viewer={viewer}
                                dataTn="image-carousel-icons"
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

PdpRespHeroCarouselComponent.defaultProps = {
    disableZoomModal: false,
    disableRequestMoreImagesModal: false,
    isCondensedCarousel: false,
    hideArrows: false,
    showDots: false,
    showCounter: true,
};

PdpRespHeroCarouselComponent.propTypes = {
    item: PropTypes.object.isRequired,
    user: PropTypes.object,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        fetchUser: PropTypes.bool.isRequired,
        isClient: PropTypes.bool,
        isTrade: PropTypes.bool,
        isCondensedPdpRedesignVariant: PropTypes.bool,
    }).isRequired,
    environment: PropTypes.object.isRequired,
    itemImage: PropTypes.string,
    itemTitle: PropTypes.string,
    viewer: PropTypes.object,
    disableZoomModal: PropTypes.bool.isRequired,
    disableRequestMoreImagesModal: PropTypes.bool.isRequired,
    isCondensedCarousel: PropTypes.bool.isRequired,
    hideArrows: PropTypes.bool.isRequired,
    showDots: PropTypes.bool.isRequired,
    selectedSkuId: PropTypes.string,
    showCounter: PropTypes.bool.isRequired,
    hideBubbles: PropTypes.bool,
    hideThumbs: PropTypes.bool,
    verticalThumbsPerPage: PropTypes.number,
};

PdpRespHeroCarouselComponent.contextType = ServerVarsContext;

export const PdpRespHeroCarousel = createFragmentContainer(PdpRespHeroCarouselComponent, {
    item: graphql`
        fragment PdpRespHeroCarousel_item on Item {
            ...PdpRespHeroCarouselRequestMoreImages_item
            ...PdpRespIconBubblesWrapper_item
            ...ViewInRoomImage_item
            ...ViewInRoomImageCredits_item
            description
            defaultSkuId
            isMultiSku
            hasPrivateListing
            isSold
            isUnpurchasable
            photos {
                largePath
                masterOrZoomPath
                alt
            }
            viewInRoomPhotos {
                src
                credits
                srcSetProps {
                    sizes
                    srcSet
                }
                ...ViewInRoomImage_viewInRoomPhoto
                ...ViewInRoomImageCredits_viewInRoomPhoto
            }
            measurement {
                convertedMeasurements(unit: IN) {
                    width
                    height
                }
                hoverDimensionsWidth: display(addLabel: false, fields: [width]) {
                    unit
                    value
                }
                hoverDimensionsHeight: display(addLabel: false, fields: [height]) {
                    unit
                    value
                }
            }
            videoVimeo {
                thumbnail(size: w100h75, includePlayButton: false, pad: false)
                status
                vimeoId
                fileByVideoSize(size: small) {
                    link
                }
            }
            reviews {
                videoReview {
                    status
                }
            }
            sortedPhotos {
                serviceId
                largePath
                masterOrZoomPath
                alt
                sortOrder
                imageSkuXrefs {
                    skuId
                }
            }
            seller {
                isAccessible
            }
        }
    `,
    user: graphql`
        fragment PdpRespHeroCarousel_user on User {
            ...PdpRespHeroCarouselRequestMoreImages_user
            ...PdpRespIconBubblesWrapper_user
            ...ViewInRoomImage_user
            ...ViewInRoomImageCredits_user
        }
    `,
    viewer: graphql`
        fragment PdpRespHeroCarousel_viewer on Viewer {
            videoUploadToolsFeatureFlag: featureFlag(feature: "videoUploadTools")
            ...PdpRespIconBubblesWrapper_viewer
        }
    `,
});
